// "Will notify" audience for the @-mention inputs (plain textarea + Tiptap).
//
// From the mentions currently in the text, works out who a save would notify
// and who would not, mirroring Mentions::Notifier on the server:
// * the writer never notifies themselves (dropped silently);
// * people already mentioned when the form loaded were notified by an earlier
//   save (the server only notifies ids that are new in this save), so they
//   are left out;
// * a colleague who can't open the context record (`can_view_context: false`
//   from GET /mentionables) is not notified, so they get a warning;
// * a token for someone who is no longer an active colleague is not notified
//   either (warning too).
//
// Per-person facts come from the picker's own search results, cached per
// context, so a picked colleague costs no extra request. Only ids the cache
// has never seen (pasted tokens, text carried over from another editor) are
// looked up, in one batch, through GET /mentionables/lookup.

const UUID_ONLY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOOKUP_LIMIT = 20;

// contextKey → Map(userId → { id, name, can_view_context } | { id, missing: true })
const known = new Map();
// contextKey → Set(userId) already requested through the lookup endpoint
const requested = new Map();

export function contextKey(contextType, contextId) {
  return contextType && contextId ? `${contextType}:${contextId}` : "";
}

function bucket(map, key, make) {
  if (!map.has(key)) map.set(key, make());
  return map.get(key);
}

// Remember picker/lookup rows for a context.
export function rememberMentionables(key, items) {
  const store = bucket(known, key, () => new Map());
  for (const item of items || []) {
    if (!item || item.type !== "user" || !UUID_ONLY.test(String(item.id || ""))) continue;
    const id = String(item.id).toLowerCase();
    store.set(id, {
      id,
      name: typeof item.name === "string" ? item.name : "",
      can_view_context: item.can_view_context ?? null
    });
  }
}

export function knownMentionables(key) {
  return bucket(known, key, () => new Map());
}

// Test hook.
export function clearMentionableCache() {
  known.clear();
  requested.clear();
}

// Unique user mentions, in order of first appearance: [{ id, label }].
export function uniqueUserMentions(tokens) {
  const seen = new Set();
  const out = [];
  for (const token of tokens || []) {
    if (!token || (token.type && token.type !== "user")) continue;
    const id = String(token.id || "").toLowerCase();
    if (!UUID_ONLY.test(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label: String(token.label || "").trim() });
  }
  return out;
}

// User mentions in a Tiptap JSON document: [{ type: "user", id, label }].
export function mentionsFromDoc(doc) {
  const out = [];
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "mention" && node.attrs?.id) {
      out.push({ type: "user", id: String(node.attrs.id), label: String(node.attrs.label || "") });
    }
    for (const child of node.content || []) walk(child);
  };
  walk(doc);
  return out;
}

// { notify: [{ id, name }], blocked: [{ id, name, reason }], pending: [id] }
// reason: "cannot_view" | "not_member". Ids not yet known count as notified
// (optimistic) and are listed in `pending` for a lookup.
export function computeAudience(tokens, { known: facts = new Map(), initialIds = [], currentUserId = null } = {}) {
  const skip = new Set([...(initialIds || [])].map((id) => String(id).toLowerCase()));
  if (currentUserId) skip.add(String(currentUserId).toLowerCase());

  const notify = [];
  const blocked = [];
  const pending = [];
  for (const { id, label } of uniqueUserMentions(tokens)) {
    if (skip.has(id)) continue;
    const fact = facts.get(id);
    const name = label || fact?.name || "";
    if (!fact) {
      pending.push(id);
      notify.push({ id, name });
    } else if (fact.missing) {
      blocked.push({ id, name, reason: "not_member" });
    } else if (fact.can_view_context === false) {
      blocked.push({ id, name, reason: "cannot_view" });
    } else {
      notify.push({ id, name });
    }
  }
  return { notify, blocked, pending };
}

// Localized warning for a blocked person. Templates carry a literal %{name}.
export function blockedMessage(item, messages = {}) {
  const template = item.reason === "not_member" ? messages.notMember : messages.cannotView;
  return String(template || "").replace("%{name}", () => item.name);
}

// Stable signature so callers only touch reactive state (and the aria-live
// region) when the audience actually changes.
export function audienceSignature({ notify = [], blocked = [] } = {}) {
  return JSON.stringify([notify.map((p) => [p.id, p.name]), blocked.map((p) => [p.id, p.name, p.reason])]);
}

// Look up ids the cache has never seen; each id is requested at most once per
// context. Resolves true when something new was learned.
export async function lookupMentionables(lookupUrl, { ids = [], contextType, contextId } = {}) {
  if (!lookupUrl) return false;
  const key = contextKey(contextType, contextId);
  const asked = bucket(requested, key, () => new Set());
  const facts = knownMentionables(key);
  const wanted = [...new Set(ids.map((id) => String(id).toLowerCase()))]
    .filter((id) => UUID_ONLY.test(id) && !asked.has(id) && !facts.has(id))
    .slice(0, LOOKUP_LIMIT);
  if (!wanted.length) return false;
  wanted.forEach((id) => asked.add(id));

  const params = new URLSearchParams({ ids: wanted.join(",") });
  if (contextType && contextId) {
    params.set("context_type", contextType);
    params.set("context_id", contextId);
  }
  try {
    const response = await fetch(`${lookupUrl}?${params.toString()}`, {
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin"
    });
    if (!response.ok) {
      wanted.forEach((id) => asked.delete(id));
      return false;
    }
    const rows = await response.json();
    rememberMentionables(key, Array.isArray(rows) ? rows : []);
    // Asked for but not returned: not an active colleague (left, or never was).
    wanted.forEach((id) => {
      if (!facts.has(id)) facts.set(id, { id, missing: true });
    });
    return true;
  } catch {
    wanted.forEach((id) => asked.delete(id));
    return false;
  }
}

// Alpine mixin shared by mentionTextarea and tiptapEditor. The host defines
// `audienceConfig()` → { enabled, lookupUrl, currentUserId, messages,
// contextType, contextId } and calls `refreshAudience(tokens)` whenever its
// content changes; shared/_mention_audience.html.erb renders the result.
export function audienceState() {
  return {
    audienceNotify: [],
    audienceBlocked: [],
    audienceInitialIds: [],
    _audienceSignature: "",
    _audienceTokens: [],

    rememberInitialAudience(tokens) {
      this.audienceInitialIds = uniqueUserMentions(tokens).map((m) => m.id);
    },

    refreshAudience(tokens) {
      // The "Ask @X to follow up" control (mention_follow_up.js) reads the
      // same tokens and facts, when the host mixes it in.
      if (typeof this.refreshFollowUp === "function") this.refreshFollowUp(tokens || []);
      const cfg = typeof this.audienceConfig === "function" ? this.audienceConfig() || {} : {};
      if (!cfg.enabled) return;
      this._audienceTokens = tokens || [];
      const key = contextKey(cfg.contextType, cfg.contextId);
      const result = computeAudience(this._audienceTokens, {
        known: knownMentionables(key),
        initialIds: this.audienceInitialIds,
        currentUserId: cfg.currentUserId
      });
      const signature = audienceSignature(result);
      if (signature !== this._audienceSignature) {
        this._audienceSignature = signature;
        this.audienceNotify = result.notify;
        this.audienceBlocked = result.blocked.map((item) => ({ ...item, message: blockedMessage(item, cfg.messages) }));
      }
      if (result.pending.length) {
        lookupMentionables(cfg.lookupUrl, {
          ids: result.pending,
          contextType: cfg.contextType,
          contextId: cfg.contextId
        }).then((learned) => {
          if (learned) this.refreshAudience(this._audienceTokens);
        });
      }
    }
  };
}
